import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import styles from './Timeline.module.scss';
import { TimeFilter } from '@/components/TimeFilter/TimeFilter.component';
import moment from 'moment-timezone';
import { useDispatch, useSelector } from 'react-redux';
import { useMemberCompanyTimezones } from '@/hooks/useMemberCompanyTimezones';
import { useTeamMemberDetailsForSelect } from '@/hooks/useTeamMemberDetailsForSelect';
import { timelineActionTypes } from '@/store/Timeline/Timeline.actionTypes';
import memberSessionsService from '@/services/memberSessions';
import { Dropdown } from '@/components/Dropdown';
import { Table } from '@/components/Table';
import { timelineTableConfig } from './Timeline.config';
import { Spinner } from 'reactstrap';
import { FiChevronLeft, FiChevronRight } from 'react-icons/fi';
import { Button, Modal, ModalHeader, ModalBody } from 'reactstrap';
import { IoRefresh } from 'react-icons/io5';
import { fontWeightEnum, Text, textVariantsEnum } from '@/components/Text';
import { TimelineScreenshotsModal } from './TimelineScreenshotsModal.component';
import { Modal as CustomModal } from '@/components/Modal';
import { TimelineChart } from './Chart';
import { getActivityColor } from './Timeline.helper';
import { formatPercent } from '@/utils/numberUtils';
import { formatTimeFromSeconds } from '@/utils/timeUtils';
import { normalizeIdleForDisplay } from '@/utils/idleDisplayUtils';
import { CiExport } from 'react-icons/ci';
import classNames from 'classnames';
import httpClient from '@/config/httpClient';
import { useHistory, useLocation } from 'react-router-dom';
import {
  useDashboardOverviewUrlWrite,
  useReportFiltersPopAndMemberHydration,
} from '@/hooks/useDashboardOverviewUrlSync';
import { getInitialReportDateRangeFromSearch } from '@/utils/dashboardOverviewUrlFilters';
import {
  collectScreenshotsFromActivities,
  getScreenshotFullSrc,
  getScreenshotPreviewSrc,
  getScreenshotRevealKey,
} from '@/utils/screenshotPrivacy';

export const TimelineComponent = () => {
  const [exportLoading, setExportLoading] = useState(false);
  const dispatch = useDispatch();
  const {
    sessionsByDay,
    loading,
    page,
    pageSize,
    totalDays,
    filters,
    exportData,
  } = useSelector((state) => {
    return state.timeline;
  });
  const team = useSelector((state) => state.team);
  const auth = useSelector((state) => state.auth);
  const location = useLocation();
  const history = useHistory();

  const teamId = team?.active?.activeTeamid;
  const companyId = auth?.profile?.activeCompany?.companyId;

  const [selectedMember, setSelectedMember] = useState(null);
  const memberId = selectedMember?.value || null;
  const {
    memberDetails,
    isLoading: membersLoading,
    isError: membersError,
    error: membersErrorObj,
    refetch: refetchMembers,
  } = useTeamMemberDetailsForSelect(teamId);

  const membersOptions = useMemo(() => {
    if (!memberDetails || typeof memberDetails !== 'object') return [];
    return Object.entries(memberDetails)
      .filter(([, m]) => !m?.deleted)
      .map(([uid, m]) => ({
        value: uid,
        label: m.employeeId
          ? `${m.userName || uid} (${m.employeeId})`
          : m.userName || uid,
        employeeId: m.employeeId,
        rate: m.rates ?? 0,
      }))
      .sort((a, b) => (a.label || '').localeCompare(b.label || ''));
  }, [memberDetails]);

  const membersOptionsRef = useRef(membersOptions);
  membersOptionsRef.current = membersOptions;

  const [range, setRange] = useState(() =>
    getInitialReportDateRangeFromSearch(
      typeof window !== 'undefined' ? window.location.search : ''
    )
  );

  const {
    urlTimezoneForHook,
    userOverrideTimezoneRef,
    setIgnoreUrlTimezone,
    onMemberFilterChange,
  } = useReportFiltersPopAndMemberHydration({
    history,
    location,
    teamId,
    currentUserId: auth?.profile?.id,
    membersOptions,
    selectedMember,
    setSelectedMember,
    setDateRange: setRange,
    membersOptionsRef,
  });

  const {
    timezoneOptions,
    selectedTimezone,
    setSelectedTimezone,
    timezonesLoading,
  } = useMemberCompanyTimezones({
    teamId,
    memberId,
    companyId,
    initialTimezone: urlTimezoneForHook || filters?.timeZone || null,
    autoSelectMemberTimezone: true,
    userOverrideTimezoneRef,
  });

  useDashboardOverviewUrlWrite({
    teamId,
    memberId: selectedMember?.value,
    dateRange: range,
    selectedTimezone,
    history,
    location,
  });

  const handleTimezoneFilterChange = useCallback(
    (value) => {
      userOverrideTimezoneRef.current = true;
      setIgnoreUrlTimezone(true);
      setSelectedTimezone(value);
    },
    [setSelectedTimezone, setIgnoreUrlTimezone, userOverrideTimezoneRef]
  );
  const [localPage, setLocalPage] = useState(page || 1);
  const PAGE_SIZE = 10;
  const [screenshotModalOpen, setScreenshotModalOpen] = useState(false);
  const [activitiesLoading, setActivitiesLoading] = useState(false);
  const [activitiesError, setActivitiesError] = useState('');
  const [sessionActivities, setSessionActivities] = useState([]);
  const [activeSessionMeta, setActiveSessionMeta] = useState(null);
  const [selectedScreenshotIndex, setSelectedScreenshotIndex] = useState(0);
  const modalActivitiesCacheRef = useRef({});

  const getModalActivitiesCacheKey = (
    actualSessionId,
    timezone,
    segStart,
    segEnd
  ) =>
    `${actualSessionId}::${timezone || 'UTC'}::${segStart || ''}::${segEnd || ''}`;

  const getEmployeeIdForUser = (userId) => {
    if (!userId) return '';
    const match = membersOptions.find((m) => m.value === userId);
    return match?.employeeId ?? userId;
  };

  const getRateForUser = (userId) => {
    const match = membersOptions.find((m) => m.value === userId);
    return match?.rate || 0;
  };

  const fetchTimeline = (nextPage = localPage) => {
    if (!selectedMember) return;
    const employeeId = getEmployeeIdForUser(selectedMember.value);
    dispatch({
      type: timelineActionTypes.GET_SESSIONS,
      payload: {
        teamId,
        userUID: selectedMember.value,
        companyId,
        employeeId,
        memberId: selectedMember.value,
        startDate: range.start,
        endDate: range.end,
        timeZone: selectedTimezone,
        page: nextPage,
        pageSize: PAGE_SIZE,
        rate: getRateForUser(selectedMember.value),
      },
    });
  };
  useEffect(() => {
    if (selectedMember) {
      modalActivitiesCacheRef.current = {};
      fetchTimeline(1);
      setLocalPage(1);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedMember, selectedTimezone, range.start, range.end]);

  const buildCsvFromDays = (days, tz, meta = null) => {
    const escape = (v) => `"${String(v == null ? '' : v).replace(/"/g, '""')}"`;
    const m = meta || {
      timezone: '-',
      memberName: '-',
      companyName: '-',
      teamName: '-',
      dateRange: '-',
    };

    const rows = [];
    (days || []).forEach((day) => {
      day.sessions.forEach((s) => {
        const { displayIdle: displayIdleSec, idlePercent } =
          normalizeIdleForDisplay(s.durationSec, s.idleSec);
        const productiveSec =
          s.productiveSec != null
            ? s.productiveSec
            : Math.max(0, (s.durationSec || 0) - displayIdleSec);
        rows.push({
          Timezone: m.timezone,
          Member: m.memberName,
          Company: m.companyName,
          Team: m.teamName,
          'Date range': m.dateRange,
          Date: day.dayLabel,
          Start: moment(s.start).tz(tz).format('hh:mm A'),
          End: s.end ? moment(s.end).tz(tz).format('hh:mm A') : '-',
          Duration: formatTimeFromSeconds(s.durationSec),
          'Idle time': formatTimeFromSeconds(displayIdleSec),
          'Productive time': formatTimeFromSeconds(productiveSec),
          'Idle %': formatPercent(idlePercent),
          'Activity %': formatPercent(s.activityPct),
          Project: s.project ?? '',
          Task: s.task ?? '',
        });
      });
    });
    const header = Object.keys(rows[0] || {});
    const lines = [
      header.join(','),
      ...rows.map((r) => header.map((h) => escape(r[h])).join(',')),
    ];
    return lines.join('\n');
  };

  const handleExport = () => {
    if (!selectedMember) return;
    const employeeId = getEmployeeIdForUser(selectedMember.value);
    dispatch({
      type: timelineActionTypes.GET_SESSIONS,
      payload: {
        teamId,
        userUID: selectedMember.value,
        companyId,
        employeeId,
        memberId: selectedMember.value,
        startDate: range.start,
        endDate: range.end,
        timeZone: selectedTimezone,
        page: localPage,
        pageSize: PAGE_SIZE,
        rate: getRateForUser(selectedMember.value),
        forExport: true,
      },
    });
  };

  useEffect(() => {
    if (exportData === undefined || exportData === null) return;
    const clearExportData = () => {
      dispatch({
        type: timelineActionTypes.SET_STATE,
        payload: { exportData: null },
      });
    };
    if (exportData.length === 0) {
      clearExportData();
      return;
    }
    const meta = {
      timezone: selectedTimezone || '-',
      memberName: selectedMember?.label ?? '-',
      companyName: auth?.profile?.activeCompany?.companyName ?? '-',
      teamName: team?.active?.Team ?? '-',
      dateRange: `${range.start} to ${range.end}`,
    };
    const csv = buildCsvFromDays(exportData, selectedTimezone, meta);
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.href = url;
    link.download = 'timeline_export.csv';
    link.click();
    URL.revokeObjectURL(url);
    clearExportData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [exportData]);

  const closeScreenshotsModal = () => {
    setScreenshotModalOpen(false);

    setActivitiesLoading(false);
    setActivitiesError('');
    setSessionActivities([]);
    setActiveSessionMeta(null);
    setSelectedScreenshotIndex(0);
  };
  // Helper function to filter activities by time range (for split segments)
  const filterActivitiesByTimeRange = (
    activities,
    segmentStart,
    segmentEnd,
    timezone
  ) => {
    if (!segmentStart || !segmentEnd) {
      return activities; // No filtering if no time range provided
    }

    const segmentStartMoment = moment(segmentStart).tz(timezone);
    const segmentEndMoment = moment(segmentEnd).tz(timezone);

    return activities.filter((activity) => {
      if (!activity.timestamp) return false;
      const activityMoment = moment(activity.timestamp).tz(timezone);
      // Include activities that are >= segmentStart and <= segmentEnd
      // This includes screenshots at the exact end time
      return (
        activityMoment.isSameOrAfter(segmentStartMoment) &&
        activityMoment.isSameOrBefore(segmentEndMoment)
      );
    });
  };

  const openScreenshotsModal = async (session, dayLabel) => {
    // Check if this is a split segment
    const isSplitSegment = session?.isSplit && session?.parentSessionId;
    const actualSessionId = isSplitSegment
      ? session.parentSessionId
      : session?.id;

    setActiveSessionMeta({
      sessionId: session?.id,
      dayLabel,
      start: session?.start,
      end: session?.end,
    });
    setActivitiesError('');
    setScreenshotModalOpen(true);

    if (!actualSessionId) {
      setActivitiesError('Session id not available.');
      setSessionActivities([]);
      return;
    }

    if (!teamId || !selectedMember?.value) {
      setActivitiesError('Missing team or user information.');
      setSessionActivities([]);
      return;
    }

    const cacheKey = getModalActivitiesCacheKey(
      actualSessionId,
      selectedTimezone,
      isSplitSegment ? session.start : null,
      isSplitSegment ? session.end : null
    );

    if (modalActivitiesCacheRef.current[cacheKey]) {
      setSessionActivities(modalActivitiesCacheRef.current[cacheKey]);
      setSelectedScreenshotIndex(0);
      setActivitiesLoading(false);
      return;
    }

    setSessionActivities([]);
    setActivitiesLoading(true);
    try {
      const employeeId = getEmployeeIdForUser(selectedMember.value);

      // Find the parent session's start time for path determination
      let sessionStartTime = session.start;
      if (isSplitSegment && sessionsByDay) {
        // Find the parent session or any segment with the same parentSessionId to get original start time
        for (const day of sessionsByDay) {
          const foundSession = day.sessions?.find(
            (s) =>
              s.id === actualSessionId ||
              (s.parentSessionId === actualSessionId && s.start)
          );
          if (foundSession?.start) {
            sessionStartTime = foundSession.start;
            break;
          }
        }
      }

      const result = await memberSessionsService.getSessionActivities({
        teamId,
        userUID: selectedMember.value,
        companyId,
        employeeId,
        sessionId: actualSessionId, // Use parent session ID for split segments
        sessionStartTime, // Pass session startTime to determine path
      });

      if (result.success) {
        let activities = result.data.activities || [];

        // If this is a split segment, filter activities by the segment's time range
        if (isSplitSegment && session.start && session.end) {
          activities = filterActivitiesByTimeRange(
            activities,
            session.start,
            session.end,
            selectedTimezone
          );
        }

        modalActivitiesCacheRef.current[cacheKey] = activities;
        setSessionActivities(activities);
        setSelectedScreenshotIndex(0);
      } else {
        setActivitiesError('Unable to load screenshots for this session.');
      }
    } catch (error) {
      console.error('Error loading session activities', error);
      setActivitiesError('Unable to load screenshots for this session.');
    } finally {
      setActivitiesLoading(false);
    }
  };

  const flattenedScreens = useMemo(() => {
    return collectScreenshotsFromActivities(sessionActivities).map((row) => ({
      ...row,
      revealKey: getScreenshotRevealKey(row),
    }));
  }, [sessionActivities]);

  const selectPrev = () => {
    if (!flattenedScreens.length) return;
    setSelectedScreenshotIndex((prev) =>
      prev === 0 ? flattenedScreens.length - 1 : prev - 1
    );
  };

  const selectNext = () => {
    if (!flattenedScreens.length) return;
    setSelectedScreenshotIndex((prev) =>
      prev === flattenedScreens.length - 1 ? 0 : prev + 1
    );
  };

  useEffect(() => {
    if (!flattenedScreens.length) {
      setSelectedScreenshotIndex(0);
    } else if (selectedScreenshotIndex >= flattenedScreens.length) {
      setSelectedScreenshotIndex(0);
    }
  }, [flattenedScreens, selectedScreenshotIndex]);

  const totalPages = Math.ceil((totalDays || 0) / PAGE_SIZE) || 1;
  const onExport = async () => {
    try {
      await httpClient.post('/exportTimelineCsv', {
        loggedInUser: {
          id: auth?.profile?.id,
          email: auth?.profile?.email,
          name: auth?.profile?.name,
        },
        teamName: team?.active?.Team,
        companyName: auth?.profile?.activeCompany?.companyName,
        memberName: selectedMember?.label,
        teamId,
        userUID: selectedMember?.value,
        companyId,
        employeeId: getEmployeeIdForUser(selectedMember?.value),
        memberId: selectedMember?.value,
        startDate: range.start,
        endDate: range.end,
        timeZone: selectedTimezone,
        rate: getRateForUser(selectedMember?.value),
      });
    } catch (error) {
      console.error('Error exporting timeline', error);
    } finally {
      setExportLoading(false);
    }
  };
  return (
    <div className={styles.timelinePage}>
      <div className={styles.filtersContainer}>
        <div className={styles.dropdownsContainer}>
          <div className={styles.dropdown}>
            <Dropdown
              options={membersOptions}
              value={selectedMember}
              label={'SELECT MEMBER'}
              onChange={onMemberFilterChange}
              isLoading={membersLoading}
              isDisabled={membersLoading || membersError}
            />
            {membersError && (
              <div className={styles.memberError}>
                <Text
                  variant={textVariantsEnum.Text_sm}
                  fontWeight={fontWeightEnum.Medium}
                >
                  Failed to load members. {membersErrorObj?.message || ''}
                </Text>
                <Button color="link" size="sm" onClick={() => refetchMembers()}>
                  Retry
                </Button>
              </div>
            )}
          </div>
          <div className={styles.dropdown}>
            <Dropdown
              label={'TIME'}
              options={timezoneOptions}
              value={
                timezoneOptions.find((t) => t.value === selectedTimezone) ||
                (selectedTimezone
                  ? { value: selectedTimezone, label: selectedTimezone }
                  : null)
              }
              onChange={({ value }) => handleTimezoneFilterChange(value)}
              isLoading={timezonesLoading}
            />
          </div>
        </div>
        <div className={styles.timeFilterContainer}>
          <TimeFilter range={range} setRange={setRange} />
        </div>
        <div
          className={styles.refreshButton}
          onClick={() => {
            fetchTimeline(1);
            setLocalPage(1);
          }}
        >
          <IoRefresh />
          <Text
            variant={textVariantsEnum.Text_sm}
            fontWeight={fontWeightEnum.Medium}
          >
            Refresh
          </Text>
        </div>
        <div
          className={classNames(styles.exportButton, {
            [styles.exportButton_disabled]: !sessionsByDay?.length,
          })}
          onClick={() => {
            setExportLoading(true);
            onExport();
          }}
        >
          {exportLoading ? (
            <Spinner color="primary" size="sm" />
          ) : (
            <>
              <CiExport />
              <Text
                variant={textVariantsEnum.Text_sm}
                fontWeight={fontWeightEnum.Medium}
              >
                Export
              </Text>
            </>
          )}
        </div>
      </div>
      {loading && (
        <div className={styles.loaderContainer}>
          <Spinner color="primary" />
        </div>
      )}
      {!loading && !sessionsByDay.length && (
        <div className={styles.noSessionsContainer}>
          No sessions found for selected filters.
        </div>
      )}
      {!loading &&
        sessionsByDay.map((day, index) => {
          const segments = day.sessions.map((session) => {
            const startTime = moment(session.start)
              .tz(selectedTimezone)
              .format('HH:mm');
            const endTime = moment(session.end)
              .tz(selectedTimezone)
              .format('HH:mm');
            const { displayIdle, idlePercent } = normalizeIdleForDisplay(
              session.durationSec,
              session.idleSec
            );
            return {
              start12H: moment(session.start)
                .tz(selectedTimezone)
                .format('hh:mm A'),
              end12H: moment(session.end)
                .tz(selectedTimezone)
                .format('hh:mm A'),
              totalTimeTracked: formatTimeFromSeconds(session.durationSec),
              idleTime: formatTimeFromSeconds(displayIdle),
              activityPct: formatPercent(session.activityPct),
              idlePercent: formatPercent(idlePercent),
              start24H: startTime,
              end24H: endTime,
              color: getActivityColor(session.activityPct, true),
            };
          });
          return (
            <div className={styles.timelineTableContainer}>
              <TimelineChart segments={segments} />

              <div>
                <h5 className="mb-0">{day.dayLabel}</h5>
                <small className="text-muted">
                  Total: {formatTimeFromSeconds(day.totals.durationSec)} | Idle:{' '}
                  {formatTimeFromSeconds(
                    normalizeIdleForDisplay(
                      day.totals.durationSec,
                      day.totals.idleSec
                    ).displayIdle
                  )}{' '}
                  | Activity: {formatPercent(day.totals.activityPct)}{' '}
                  {day.totals.earnings !== null &&
                    day.totals.earnings !== undefined && (
                      <>| Earnings: ${day.totals.earnings}</>
                    )}
                </small>
              </div>
              <div className={styles.tableContainer}>
                <Table
                  data={day.sessions}
                  columns={timelineTableConfig({
                    selectedTimezone,
                    openScreenshotsModal,
                    dayLabel: day.dayLabel,
                  })}
                  className={styles.timelineTable}
                />
              </div>
            </div>
          );
        })}
      <CustomModal
        isVisible={screenshotModalOpen}
        onClose={closeScreenshotsModal}
      >
        <TimelineScreenshotsModal
          activitiesLoading={activitiesLoading}
          activitiesError={activitiesError}
          flattenedScreens={flattenedScreens}
          selectedScreenshotIndex={selectedScreenshotIndex}
          selectedTimezone={selectedTimezone}
          setSelectedScreenshotIndex={setSelectedScreenshotIndex}
          activeSessionMeta={activeSessionMeta}
        />
      </CustomModal>
      <Modal isOpen={false} toggle={closeScreenshotsModal} size="lg">
        <ModalHeader toggle={closeScreenshotsModal}>
          Screenshots{' '}
          {activeSessionMeta?.dayLabel ? `- ${activeSessionMeta.dayLabel}` : ''}
        </ModalHeader>
        <ModalBody style={{ paddingLeft: '16px', paddingRight: '16px' }}>
          {activitiesLoading && (
            <div className="text-center my-3">
              <Spinner color="primary" />
            </div>
          )}
          {!activitiesLoading && activitiesError && (
            <div className="text-danger">{activitiesError}</div>
          )}
          {!activitiesLoading &&
            !activitiesError &&
            flattenedScreens.length === 0 && (
              <div className="text-muted">
                No screenshots available for this session.
              </div>
            )}
          {!activitiesLoading &&
            !activitiesError &&
            flattenedScreens.length > 0 && (
              <div>
                <div
                  className="d-flex align-items-center justify-content-center mb-3 position-relative"
                  style={{
                    minHeight: '280px',
                    background: '#f8f9fa',
                    border: '1px solid #e9ecef',
                    borderRadius: '8px',
                    padding: '12px',
                  }}
                >
                  <Button
                    color="secondary"
                    outline
                    size="sm"
                    onClick={selectPrev}
                    style={{
                      position: 'absolute',
                      left: 12,
                      top: '50%',
                      transform: 'translateY(-50%)',
                      width: '44px',
                      height: '44px',
                      padding: 0,
                      borderRadius: '50%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      background: 'rgba(0,0,0,0.55)',
                      color: '#fff',
                      boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
                      border: 'none',
                    }}
                    aria-label="Previous screenshot"
                  >
                    <FiChevronLeft size={22} />
                  </Button>
                  <div className="text-center w-100">
                    <img
                      src={getScreenshotFullSrc(
                        flattenedScreens[selectedScreenshotIndex]
                      )}
                      alt="Screenshot preview"
                      style={{
                        maxHeight: '60vh',
                        maxWidth: '100%',
                        objectFit: 'contain',
                        borderRadius: '6px',
                      }}
                    />
                    <div className="mt-2">
                      <div className="font-weight-bold">
                        {flattenedScreens[selectedScreenshotIndex]?.timestamp
                          ? moment(
                              flattenedScreens[selectedScreenshotIndex]
                                .timestamp
                            )
                              .tz(selectedTimezone)
                              .format('DD MMM YYYY, hh:mm A')
                          : 'Timestamp unavailable'}
                      </div>
                      <small className="text-muted">
                        {`Screenshot ${selectedScreenshotIndex + 1} of ${
                          flattenedScreens.length
                        }`}
                      </small>
                      <div>
                        <a
                          href={getScreenshotFullSrc(
                            flattenedScreens[selectedScreenshotIndex]
                          )}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          Open in new tab
                        </a>
                      </div>
                    </div>
                  </div>
                  <Button
                    color="secondary"
                    outline
                    size="sm"
                    onClick={selectNext}
                    style={{
                      position: 'absolute',
                      right: 12,
                      top: '50%',
                      transform: 'translateY(-50%)',
                      width: '44px',
                      height: '44px',
                      padding: 0,
                      borderRadius: '50%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      background: 'rgba(0,0,0,0.55)',
                      color: '#fff',
                      boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
                      border: 'none',
                    }}
                    aria-label="Next screenshot"
                  >
                    <FiChevronRight size={22} />
                  </Button>
                </div>
                <div>
                  <div className="mb-2 font-weight-bold">All screenshots</div>
                  <div
                    style={{
                      display: 'grid',
                      gridTemplateColumns:
                        'repeat(auto-fit, minmax(140px, 1fr))',
                      gap: '12px',
                    }}
                  >
                    {flattenedScreens.map((item, idx) => (
                      <div
                        key={`${item.activityId || 'activity'}-${idx}`}
                        className="text-center"
                        style={{
                          border:
                            idx === selectedScreenshotIndex
                              ? '2px solid #007bff'
                              : '1px solid #e9ecef',
                          borderRadius: '6px',
                          padding: '6px',
                          cursor: 'pointer',
                          background:
                            idx === selectedScreenshotIndex
                              ? '#e9f2ff'
                              : '#fff',
                        }}
                        onClick={() => setSelectedScreenshotIndex(idx)}
                      >
                        <img
                          src={getScreenshotPreviewSrc(item)}
                          alt="Screenshot thumb"
                          style={{
                            width: '100%',
                            height: '120px',
                            objectFit: 'cover',
                            borderRadius: '4px',
                          }}
                        />
                        <small className="d-block mt-1 text-truncate">
                          {item.timestamp
                            ? moment(item.timestamp)
                                .tz(selectedTimezone)
                                .format('hh:mm A')
                            : 'No time'}
                        </small>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}
        </ModalBody>
      </Modal>
    </div>
  );
};
